//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import { FeatureNames } from '@bvengineering/curalate-features';
import { PinterestGatewayUser } from '@bvengineering/pinterest-gateway-service-types';

import {
  ContentLibraryContentGroup,
  ContentLibrarySourcesGroups,
  IContentLibrarySourcesOptions,
} from '@apis/content-library/contentLibraryApiModel';
import { IUserInfo } from '@apis/team-management/teamManagementApiModel';
import {
  DEFAULT_SOURCES_LABEL,
  MAX_DISPLAY_SOURCES,
  MAX_DISPLAY_SOURCES_SUMMARY_LENGTH,
  MediaLibrarySourceDisplayText,
  MediaLibrarySourcesConfigs,
  MediaLibrarySourcesDropdownItemData,
  MediaLibrarySourcesDropdownItemGroup,
  MediaLibrarySourcesDropdownItemString,
  MediaLibrarySourcesFeatureFlags,
  MediaLibrarySourcesInstagramConfigs,
} from '@media-library/components/top-bar/sources-dropdown/mediaLibrarySourcesDropdownConstants';
import { EmptySources } from '@media-library/constants/initialMediaLibraryItemParams';
import UserId from '@models/UserId';
import { isDefined, isUndefined } from '@utils/filterPredicates';

import { hasFeature } from 'app/initialization/routing/stateHelpers';

/**
 * Check if at least one descendant is selected.
 * @param subItems Array of sub-items
 * @returns true if at least one its descendant is selected
 */
export const hasSelectedDescendant = (
  subItems: MediaLibrarySourcesDropdownItemData[],
): boolean => {
  return subItems.some(({ isIndeterminate }) => isIndeterminate);
};

/**
 * Determine whether a media library source is selected by assessing its descendants.
 * @param source Media library source to be determined. States of sub-items must be up-to-date.
 * @returns true if source is indeterminate and all sub-items are selected
 */
export const shouldBeSelected = (
  source: MediaLibrarySourcesDropdownItemData,
): boolean => {
  return (
    source.isIndeterminate &&
    (isUndefined(source.subItems) ||
      source.subItems.every(({ isSelected }) => isSelected))
  );
};

/**
 * Check if source label matches search term.
 * Only apply to team members, tracked instagram competitors, hashtags and users.
 * Always return true for other source types.
 * @param source Media library sources dropdown item
 * @param searchTerm Search term
 * @returns true if search term exists in source label
 */
export const filterSources = (
  source: MediaLibrarySourcesDropdownItemData,
  searchTerm: string,
): boolean => {
  const { label, paramKey, value } = source;

  switch (paramKey) {
    case 'curalateUserIds':
    case 'trackedHashtags':
    case 'trackedBusinessUserIds':
    case 'trackedUserIds':
      return label.toLowerCase().includes(searchTerm.toLowerCase().trim());
    case 'sourcesGroups':
      return (
        searchTerm === '' ||
        [
          ContentLibrarySourcesGroups.TEAM_MEDIA,
          ContentLibrarySourcesGroups.ALL_INSTAGRAM,
          ContentLibrarySourcesGroups.ALL_TRACKED_COMPETITORS,
          ContentLibrarySourcesGroups.ALL_TRACKED_HASHTAGS,
          ContentLibrarySourcesGroups.ALL_TRACKED_USERS,
        ].includes(value)
      );
    default:
      return searchTerm === '';
  }
};

/**
 * Map media library source dropdown states to API params recursively.
 * All descendants will be ignored if parent item is selected.
 * @param allMedia Media library sources dropdown root item
 * @returns sources to be used in API params
 */
export const mapDropdownStateToSourcesOptions = (
  allMedia: MediaLibrarySourcesDropdownItemGroup,
): IContentLibrarySourcesOptions => {
  const sourcesOptions = structuredClone(EmptySources);

  const mapSource = (source: MediaLibrarySourcesDropdownItemData) => {
    const { isIndeterminate, isSelected, paramKey, subItems, value } = source;

    if (!isIndeterminate) {
      return;
    }

    if (isSelected) {
      switch (paramKey) {
        case 'accountMentionIds':
        case 'curalateUserIds':
        case 'pinterestUserIds':
        case 'trackedBusinessUserIds':
        case 'trackedHashtags':
        case 'trackedUserIds':
          isDefined(sourcesOptions[paramKey])
            ? sourcesOptions[paramKey].push(value)
            : (sourcesOptions[paramKey] = [value]);
          return;
        case 'sourcesGroups':
          isDefined(sourcesOptions[paramKey])
            ? sourcesOptions[paramKey].push(value)
            : (sourcesOptions[paramKey] = [value]);
          return;
        case 'brandFeedPost':
        case 'brandStoryPost':
          sourcesOptions[paramKey] = value;
          return;
      }
    }

    subItems?.forEach((subItem) => mapSource(subItem));
  };

  mapSource(allMedia);

  sourcesOptions.contentGroup = ContentLibraryContentGroup.NOTHING;

  return sourcesOptions;
};

interface MapAllSourcesParams {
  currentUserId: UserId;
  featureFlags: MediaLibrarySourcesFeatureFlags;
  selectedSources: IContentLibrarySourcesOptions;
  sourcesConfigs: MediaLibrarySourcesConfigs;
}

/**
 * Map available media library sources to dropdown items.
 * Get `isSelected` state from `selectedSources`.
 * Invalid values in `selectedSources` would be skipped.
 * @param param
 * @param param.featureFlags Feature flags
 * @param param.selectedSources Selected sources
 * @param param.sourcesConfigs Client's available media library sources
 * @param param.sourcesConfigs.instagramConfigs Client's tracked Instagram sources
 * @param param.sourcesConfigs.pinterestUser Client's Pinterest account
 * @param param.sourcesConfigs.teamMembers Team members under client
 * @param param.currentUserId Current user id
 * @returns Root item of media library sources dropdown
 */
export const mapAllSources = ({
  currentUserId,
  featureFlags,
  selectedSources,
  sourcesConfigs: { instagramConfigs, pinterestUser, teamMembers },
}: MapAllSourcesParams): MediaLibrarySourcesDropdownItemGroup => {
  const { sourcesGroups = [] } = selectedSources;

  const selectAllMedia = sourcesGroups.includes(
    ContentLibrarySourcesGroups.ALL_MEDIA,
  );

  const subSourceGroups: MediaLibrarySourcesDropdownItemGroup[] = [
    mapTeamSources({ currentUserId, teamMembers, selectedSources }),
    mapInstagramSources({ instagramConfigs, selectedSources, featureFlags }),
  ];

  if (isDefined(pinterestUser)) {
    subSourceGroups.push(mapPinterestSources(pinterestUser, selectedSources));
  }

  if (featureFlags.isDamImportDropboxEnabled) {
    subSourceGroups.push(mapDropboxSources(selectedSources));
  }

  subSourceGroups.push(mapTikTokSources(selectedSources));

  if (featureFlags.isYouTubeEnabled) {
    subSourceGroups.push(mapYouTubeSources(selectedSources));
  }

  const hasSelectedSource =
    selectAllMedia || hasSelectedDescendant(subSourceGroups);

  return {
    isIndeterminate: hasSelectedSource,
    isSelected: selectAllMedia,
    label: 'All Media',
    paramKey: 'sourcesGroups',
    subItems: subSourceGroups,
    value: ContentLibrarySourcesGroups.ALL_MEDIA,
  };
};

interface MapTeamSourcesParams {
  currentUserId: UserId;
  teamMembers: IUserInfo[];
  selectedSources: IContentLibrarySourcesOptions;
}
/**
 * Map team members' info to dropdown items.
 * Get `isSelected` state from `selectedSources`.
 * Invalid values in `selectedSources` would be skipped.
 * @param param
 * @param param.teamMembers Team members under client
 * @param param.selectedSources Selected sources
 * @param param.currentUserId Current user id
 * @returns Root item for all team media sources
 */
const mapTeamSources = ({
  currentUserId,
  selectedSources,
  teamMembers,
}: MapTeamSourcesParams): MediaLibrarySourcesDropdownItemGroup => {
  const { curalateUserIds = [], sourcesGroups = [] } = selectedSources;

  const selectAllTeamMedia =
    sourcesGroups.includes(ContentLibrarySourcesGroups.ALL_MEDIA) ||
    sourcesGroups.includes(ContentLibrarySourcesGroups.TEAM_MEDIA);

  const allTeamMembers = teamMembers.map<MediaLibrarySourcesDropdownItemString>(
    ({ firstName, id, lastName }) => {
      const fullName = `${firstName} ${lastName}`.trim();
      const userId = id.toString();
      const isSelected = selectAllTeamMedia || curalateUserIds.includes(userId);

      return {
        isIndeterminate: isSelected,
        isSelected: isSelected,
        label:
          currentUserId.toString() === userId ? `${fullName} (You)` : fullName,
        paramKey: 'curalateUserIds',
        value: userId,
      };
    },
  );

  return {
    isIndeterminate:
      selectAllTeamMedia || hasSelectedDescendant(allTeamMembers),
    isSelected: selectAllTeamMedia,
    label: 'Team Media',
    paramKey: 'sourcesGroups',
    subItems: sortBySelectedAndLabel(allTeamMembers),
    value: ContentLibrarySourcesGroups.TEAM_MEDIA,
  };
};

interface MapInstagramSourcesParams {
  featureFlags: MediaLibrarySourcesFeatureFlags;
  instagramConfigs: MediaLibrarySourcesInstagramConfigs;
  selectedSources: IContentLibrarySourcesOptions;
}

/**
 * Map all Instagram sources to dropdown items.
 * @param param
 * @param param.featureFlags Feature flags
 * @param param.instagramConfigs Client's available Instagram sources
 * @param param.instagramConfigs.defaultInstagramAccount Client's default Instagram account
 * @param param.instagramConfigs.trackedBusinessUsers Client's tracked Instagram business accounts
 * @param param.instagramConfigs.trackedCompetitors Client's tracked Instagram competitor accounts
 * @param param.instagramConfigs.trackedHashtags Client's tracked Instagram hashtags
 * @param param.instagramConfigs.trackedUsers Clients tracked Instagram regular accounts
 * @param param.selectedSources Selected sources
 * @returns Root item for all Instagram sources
 */
const mapInstagramSources = ({
  featureFlags: { isInstagramCompetitorsEnabled, isMultiHandleFeatureEnabled },
  instagramConfigs: {
    connectedInstagramAccounts,
    defaultInstagramAccount,
    trackedBusinessUsers,
    trackedCompetitors,
    trackedHashtags,
    trackedUsers,
  },
  selectedSources,
}: MapInstagramSourcesParams): MediaLibrarySourcesDropdownItemGroup => {
  const {
    accountMentionIds = [],
    brandFeedPost = false,
    brandStoryPost = false,
    sourcesGroups = [],
    trackedBusinessUserIds = [],
    trackedHashtags: trackedHashtagIds = [],
    trackedUserIds = [],
  } = selectedSources;

  const selectAllInstagram =
    sourcesGroups.includes(ContentLibrarySourcesGroups.ALL_MEDIA) ||
    sourcesGroups.includes(ContentLibrarySourcesGroups.ALL_INSTAGRAM);

  const selectAllBrandPostSources =
    selectAllInstagram ||
    sourcesGroups.includes(ContentLibrarySourcesGroups.ALL_BRAND_POSTS);
  const hasSelectedBrandPostSource =
    selectAllBrandPostSources || brandFeedPost || brandStoryPost;

  const selectDefaultAccount =
    selectAllInstagram ||
    (accountMentionIds.length > 0 &&
      accountMentionIds[0] === defaultInstagramAccount?.accountId);

  const selectAllTrackedUsers =
    selectAllInstagram ||
    sourcesGroups.includes(ContentLibrarySourcesGroups.ALL_TRACKED_USERS);

  const selectAllTrackedCompetitors =
    selectAllInstagram ||
    sourcesGroups.includes(ContentLibrarySourcesGroups.ALL_TRACKED_COMPETITORS);

  const selectAllTrackedHashtags =
    selectAllInstagram ||
    sourcesGroups.includes(ContentLibrarySourcesGroups.ALL_TRACKED_HASHTAGS);

  const instagramSubSourceGroups: MediaLibrarySourcesDropdownItemData[] = [];

  const connectedUserIds = connectedInstagramAccounts?.map((acc) => acc.userId);
  const userIdsString = connectedUserIds ? connectedUserIds.join(',') : '';

  const connectedUsers =
    connectedInstagramAccounts?.map<MediaLibrarySourcesDropdownItemString>(
      (account) => {
        const isSelected =
          selectAllInstagram || accountMentionIds.includes(account.userId);
        return {
          isIndeterminate: isSelected,
          isSelected: isSelected,
          label: `@${account.username}`,
          paramKey: 'accountMentionIds',
          value: account.userId,
        };
      },
    );

  if (isDefined(defaultInstagramAccount)) {
    instagramSubSourceGroups.push({
      isIndeterminate: hasSelectedBrandPostSource,
      isSelected: selectAllBrandPostSources,
      label: 'Your Account',
      paramKey: 'sourcesGroups',
      subItems: [
        {
          isIndeterminate: selectAllBrandPostSources || brandFeedPost,
          isSelected: selectAllBrandPostSources || brandFeedPost,
          label: 'Feed Posts',
          paramKey: 'brandFeedPost',
          value: true,
        },
        {
          isIndeterminate: selectAllBrandPostSources || brandStoryPost,
          isSelected: selectAllBrandPostSources || brandStoryPost,
          label: 'Story Posts',
          paramKey: 'brandStoryPost',
          value: true,
        },
      ],
      value: ContentLibrarySourcesGroups.ALL_BRAND_POSTS,
    });

    if (isMultiHandleFeatureEnabled) {
      instagramSubSourceGroups.push({
        isIndeterminate:
          selectAllInstagram ||
          (isDefined(connectedUsers) && hasSelectedDescendant(connectedUsers)),
        isSelected:
          selectAllInstagram ||
          (isDefined(connectedUsers) && hasSelectedDescendant(connectedUsers)),
        label: 'Mentioned & Tagged',
        paramKey: 'accountMentionIds',
        subItems: connectedUsers ? sortBySelectedAndLabel(connectedUsers) : [],
        value: userIdsString,
      });
    } else {
      instagramSubSourceGroups.push({
        isIndeterminate: selectDefaultAccount,
        isSelected: selectDefaultAccount,
        label: 'Mentioned & Tagged',
        paramKey: 'accountMentionIds',
        value: defaultInstagramAccount.accountId,
      });
    }
  }

  const businessUsers =
    trackedBusinessUsers.map<MediaLibrarySourcesDropdownItemString>(
      ({ id, name }) => {
        const isSelected =
          selectAllTrackedUsers || trackedBusinessUserIds.includes(id);

        return {
          isIndeterminate: isSelected,
          isSelected: isSelected,
          label: `@${name}`,
          paramKey: 'trackedBusinessUserIds',
          value: id,
        };
      },
    );

  const regularUsers = trackedUsers.map<MediaLibrarySourcesDropdownItemString>(
    ({ id, name }) => {
      const isSelected = selectAllTrackedUsers || trackedUserIds.includes(id);

      return {
        paramKey: 'trackedUserIds',
        label: `@${name}`,
        value: id,
        isIndeterminate: isSelected,
        isSelected: isSelected,
      };
    },
  );

  const allUsers = [...businessUsers, ...regularUsers];

  const allTrackedUsers: MediaLibrarySourcesDropdownItemGroup = {
    isIndeterminate: selectAllTrackedUsers || hasSelectedDescendant(allUsers),
    isSelected: selectAllTrackedUsers,
    label: 'Tracked Users',
    paramKey: 'sourcesGroups',
    subItems: sortBySelectedAndLabel(allUsers),
    value: ContentLibrarySourcesGroups.ALL_TRACKED_USERS,
  };

  instagramSubSourceGroups.push(allTrackedUsers);

  if (isInstagramCompetitorsEnabled) {
    const allCompetitors =
      trackedCompetitors.map<MediaLibrarySourcesDropdownItemString>(
        ({ id, username }) => {
          const isSelected =
            selectAllTrackedCompetitors || trackedBusinessUserIds.includes(id);

          return {
            isIndeterminate: isSelected,
            isSelected: isSelected,
            label: username ?? '',
            paramKey: 'trackedBusinessUserIds',
            value: id,
          };
        },
      );

    const allTrackedCompetitors: MediaLibrarySourcesDropdownItemGroup = {
      isIndeterminate:
        selectAllTrackedCompetitors || hasSelectedDescendant(allCompetitors),
      isSelected: selectAllTrackedCompetitors,
      label: 'Tracked Competitors',
      paramKey: 'sourcesGroups',
      subItems: sortBySelectedAndLabel(allCompetitors),
      value: ContentLibrarySourcesGroups.ALL_TRACKED_COMPETITORS,
    };

    instagramSubSourceGroups.push(allTrackedCompetitors);
  }

  const allHashtags =
    trackedHashtags.map<MediaLibrarySourcesDropdownItemString>((hashtag) => {
      const { id, name } = hashtag;
      const isSelected =
        selectAllTrackedHashtags || trackedHashtagIds.includes(id);

      return {
        paramKey: 'trackedHashtags',
        label: `#${name}`,
        value: hashtag.id,
        isIndeterminate: isSelected,
        isSelected: isSelected,
      };
    });

  const allTrackedHashtags: MediaLibrarySourcesDropdownItemGroup = {
    isIndeterminate:
      selectAllTrackedHashtags || hasSelectedDescendant(allHashtags),
    isSelected: selectAllTrackedHashtags,
    label: 'Tracked Hashtags',
    paramKey: 'sourcesGroups',
    subItems: sortBySelectedAndLabel(allHashtags),
    value: ContentLibrarySourcesGroups.ALL_TRACKED_HASHTAGS,
  };

  if (hasFeature(FeatureNames.HashtagTrackingAndInsights)) {
    instagramSubSourceGroups.push(allTrackedHashtags);
  }

  return {
    isIndeterminate:
      selectAllInstagram || hasSelectedDescendant(instagramSubSourceGroups),
    isSelected: selectAllInstagram,
    label: 'Instagram',
    paramKey: 'sourcesGroups',
    subItems: instagramSubSourceGroups,
    value: ContentLibrarySourcesGroups.ALL_INSTAGRAM,
  };
};

/**
 * Map all Pinterest sources to dropdown items.
 * @param pinterestUser Client's Pinterest account
 * @param selectedSources Selected sources
 * @returns Root item for all Pinterest sources
 */
const mapPinterestSources = (
  pinterestUser: PinterestGatewayUser,
  selectedSources: IContentLibrarySourcesOptions,
): MediaLibrarySourcesDropdownItemGroup => {
  const { pinterestUserIds = [], sourcesGroups = [] } = selectedSources;

  const selectAllPinterest =
    sourcesGroups.includes(ContentLibrarySourcesGroups.ALL_MEDIA) ||
    sourcesGroups.includes(ContentLibrarySourcesGroups.ALL_PINTEREST);
  const selectDefaultPinterest =
    selectAllPinterest || pinterestUserIds.includes(pinterestUser.id);

  const defaultPinterestAccount: MediaLibrarySourcesDropdownItemString = {
    isIndeterminate: selectDefaultPinterest,
    isSelected: selectDefaultPinterest,
    label: 'Your Account',
    paramKey: 'pinterestUserIds',
    value: pinterestUser.id,
  };

  const pinterestSubSourceGroups: MediaLibrarySourcesDropdownItemData[] = [
    defaultPinterestAccount,
  ];

  return {
    isIndeterminate:
      selectAllPinterest || hasSelectedDescendant(pinterestSubSourceGroups),
    isSelected: selectAllPinterest,
    label: 'Pinterest',
    paramKey: 'sourcesGroups',
    subItems: pinterestSubSourceGroups,
    value: ContentLibrarySourcesGroups.ALL_PINTEREST,
  };
};

/**
 * Map all Dropbox sources to dropdown items.
 * @param selectedSources Selected sources
 * @returns Root item for all Dropbox sources
 */

const mapDropboxSources = (
  selectedSources: IContentLibrarySourcesOptions,
): MediaLibrarySourcesDropdownItemGroup => {
  const { sourcesGroups = [] } = selectedSources;

  const selectAllDropbox =
    sourcesGroups.includes(ContentLibrarySourcesGroups.ALL_MEDIA) ||
    sourcesGroups.includes(ContentLibrarySourcesGroups.ALL_DROPBOX);

  return {
    isIndeterminate: selectAllDropbox,
    isSelected: selectAllDropbox,
    label: 'Dropbox',
    paramKey: 'sourcesGroups',
    subItems: [],
    value: ContentLibrarySourcesGroups.ALL_DROPBOX,
  };
};

const mapTikTokSources = (
  selectedSources: IContentLibrarySourcesOptions,
): MediaLibrarySourcesDropdownItemGroup => {
  const { sourcesGroups = [] } = selectedSources;

  const selectAllTikTok =
    sourcesGroups.includes(ContentLibrarySourcesGroups.ALL_MEDIA) ||
    sourcesGroups.includes(ContentLibrarySourcesGroups.ALL_TIKTOK);

  return {
    isIndeterminate: selectAllTikTok,
    isSelected: selectAllTikTok,
    label: 'TikTok',
    paramKey: 'sourcesGroups',
    subItems: [],
    value: ContentLibrarySourcesGroups.ALL_TIKTOK,
  };
};

const mapYouTubeSources = (
  selectedSources: IContentLibrarySourcesOptions,
): MediaLibrarySourcesDropdownItemGroup => {
  const { sourcesGroups = [] } = selectedSources;

  const selectAllYouTube =
    sourcesGroups.includes(ContentLibrarySourcesGroups.ALL_MEDIA) ||
    sourcesGroups.includes(ContentLibrarySourcesGroups.ALL_YOUTUBE);

  return {
    isIndeterminate: selectAllYouTube,
    isSelected: selectAllYouTube,
    label: 'YouTube',
    paramKey: 'sourcesGroups',
    subItems: [],
    value: ContentLibrarySourcesGroups.ALL_YOUTUBE,
  };
};

/**
 * Sort media library sources dropdown items by selected state then in alphanumeric order.
 * @param sources Array of media library sources dropdown items
 * @returns Sorted array
 */
export const sortBySelectedAndLabel = <
  T extends MediaLibrarySourcesDropdownItemData,
>(
  sources: T[],
): T[] => {
  return sources.sort((a, b) => {
    if (a.isSelected === b.isSelected) {
      return a.label.toLowerCase().localeCompare(b.label.toLowerCase());
    }

    return a.isSelected ? -1 : 1;
  });
};

/**
 * Get sources label and summary for dropdown button.
 * @param allMedia Root item of media library sources dropdown
 * @returns Label for dropdown button and selected sources summary
 */
export const computeSourcesSummary = (
  allMedia: MediaLibrarySourcesDropdownItemGroup,
) => {
  const MediaLibrarySourceDisplayTexts =
    computeSourcesDisplayTextFromAllMedia(allMedia);

  if (MediaLibrarySourceDisplayTexts.length === 0) {
    return DEFAULT_SOURCES_LABEL;
  }

  const sourcesSummary = MediaLibrarySourceDisplayTexts.join('; ');
  const sourcesCount = MediaLibrarySourceDisplayTexts.length;

  return sourcesCount <= MAX_DISPLAY_SOURCES &&
    sourcesSummary.length <= MAX_DISPLAY_SOURCES_SUMMARY_LENGTH
    ? sourcesSummary
    : `${sourcesCount} Sources`;
};

/**
 * Get list of display texts for selected sources from dropdown states.
 * @param allMedia Root item of media library sources dropdown
 * @returns Array of display texts
 */
const computeSourcesDisplayTextFromAllMedia = (
  allMedia: MediaLibrarySourcesDropdownItemGroup,
) => {
  const sourceGroupDisplayTexts: string[] = [];
  const idDisplayTexts: string[] = [];
  const specialDisplayTexts: string[] = [];

  const computeSourcesDisplayTextFromSource = ({
    isIndeterminate,
    isSelected,
    label,
    paramKey,
    subItems = [],
    value,
  }: MediaLibrarySourcesDropdownItemData) => {
    if (!isIndeterminate) {
      return;
    }

    if (!isSelected) {
      subItems.forEach((subItem) =>
        computeSourcesDisplayTextFromSource(subItem),
      );
      return;
    }

    switch (paramKey) {
      case 'accountMentionIds':
      case 'brandFeedPost':
      case 'brandStoryPost':
      case 'pinterestUserIds':
        specialDisplayTexts.push(
          MediaLibrarySourceDisplayText[
            paramKey as keyof typeof MediaLibrarySourceDisplayText
          ],
        );
        return;
      case 'sourcesGroups':
        sourceGroupDisplayTexts.push(
          MediaLibrarySourceDisplayText[
            value as keyof typeof MediaLibrarySourceDisplayText
          ],
        );
        return;
      case 'curalateUserIds':
      case 'trackedBusinessUserIds':
      case 'trackedHashtags':
      case 'trackedUserIds':
        idDisplayTexts.push(label);
        return;
    }
  };

  computeSourcesDisplayTextFromSource(allMedia);

  return [
    ...sourceGroupDisplayTexts,
    ...idDisplayTexts,
    ...specialDisplayTexts,
  ];
};
